import { Button } from "@mui/material";
import { updateApplication } from "apis/controllers/applications";
import { FileProvider, useFileContext } from "context/FilesV2";
import { useLoading } from "hooks/useLoading";
import ConfirmDialog from "qubCommon/Dialogs/ConfirmationDialog";
import FilePreviewer from "qubCommon/FilePreviewer";
import { useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { deleteBundle, deleteFile, downloadFile, uploadFiles } from "util";
import "./ApplicationDetails.css";
import AlertBanner from "./Components/AlertBanner";
import Amendment from "./Components/Amendment";
import ApplicationActions from "./Components/ApplicationActions";
import ApplicationAttachments from "./Components/ApplicationAttachments";
import ChildAttachments from "./Components/ChildAttachments";
import Children from "./Components/Children";
import Fathers from "./Components/Fathers";
import History from "./Components/History";
import ListFileItem from "./Components/ListFileItem";
import MothersInfo from "./Components/MothersInfo";
import SummaryCard from "./Components/SummaryCard";
import useApplicationDetails from "./useApplicationDetails";
import ChangeIban from "./Components/ChangeIban";

const url = import.meta.env.VITE_REACT_APP_BASE_URL;

const LEGACY_PHONE_SEASON_ID = 2025;
const STUDENT_PLUS_18_CATEGORY_ID = 7;

const hasValue = (value) => value !== undefined && value !== null && value !== "";
const isValidMobilePhone = (value) => /^69\d{8}$/.test(String(value || "").trim());

const isEmailVerificationError = (message) => {
  const text = String(message || "").toLocaleLowerCase("el-GR");
  return text.includes("email") && text.includes("επιβεβαι");
};

const findLookupById = (options, id) =>
  (options || []).find((option) => String(option?.id) === String(id)) || null;

const ApplicationDetails = () => {
  const {
    form,
    data,
    succeeded,
    expensesForm,
    files,
    fetchApplication,
    resetForm,
  } = useApplicationDetails();

  const { id } = useParams();

  if (succeeded)
    return (
      <div className="applications-container">
        <FileProvider
          downloadFile={(fileId, type) => downloadFile(fileId, type)}
          uploadFiles={(fileGroups) => uploadFiles(fileGroups, null)}
          onDeleteBundle={async (bundleId) =>
            await deleteBundle(bundleId, 4, null, id, null)
          }
          onDeleteFile={async (fileId) => {
            await deleteFile(fileId, 4, null, id, null);
          }}
        >
          <Form
            form={form}
            expensesForm={expensesForm}
            data={data}
            files={files}
            fetchApplication={fetchApplication}
            resetForm={resetForm}
          />
        </FileProvider>
      </div>
    );
};

const Form = (props) => {
  const { form, expensesForm, data, files, fetchApplication, resetForm } =
    props;
  const [openAmendment, setOpenAmendment] = useState(false);
  const [openChangeIban, setOpenChangeIban] = useState(false);
  const [fileToPreview, setFileToPreview] = useState(null);
  const [errorBanner, setErrorBanner] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogProps, setDialogProps] = useState({
    title: "",
    content: "",
    onConfirm: () => { },
    extraButtons: [],
  });
  const { id } = useParams();
  const { control } = useFileContext();
  const childrenFileRefs = useRef([]);
  const applicationFileRef = useRef();

  const { setLoading } = useLoading();
  const settings = data?.application?.settings;
  const usesLegacyPhoneFields =
    Number(data?.application?.season?.id) === LEGACY_PHONE_SEASON_ID;
  const [emailVerificationRequired, setEmailVerificationRequired] =
    useState(false);
  const shouldShowEmailVerification =
    settings?.shouldSendEmailVerification === true;
  const isLoadedApplicantEmailVerified =
    settings?.shouldSendEmailVerification === false;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const applicantMunicipalityId = form.watch("applicant.address.municipality.id");
  const applicantMunicipality =
    findLookupById(data?.lookups?.municipalities, applicantMunicipalityId) ||
    data?.application?.applicant?.address?.municipality;

  const getApplicationFormValues = () => {
    const { applicantEmailVerification, childSchoolSelections, ...values } =
      form.getValues();

    return values;
  };

  // useEffect(() => {
  //   try {
  //     const formattedFiles = formatFiles(files);
  //     if (formattedFiles !== undefined) {
  //       control.initialize(formattedFiles);
  //     } else {
  //       control.initialize([]);
  //     }
  //   } catch (error) {
  //     console.error("error", error);
  //   }

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [files]);

  const isApplicantEmailVerified = () => {
    const applicant = form.getValues("applicant");
    const verification = form.getValues("applicantEmailVerification");

    return (
      hasValue(applicant?.email) &&
      verification?.isVerified === true &&
      verification?.verifiedEmail === applicant.email
    );
  };

  const shouldRequireApplicantEmailVerification = () =>
    shouldShowEmailVerification ||
    isLoadedApplicantEmailVerified ||
    emailVerificationRequired;

  const handleValidate = ({ requireEmailVerification = false } = {}) => {
    const applicant = form.getValues("applicant");
    const husbands = form.getValues("husbands");
    const children = form.getValues("children");
    const childrenHelp = form.getValues("childrenHelp") || [];

    const errors = [];

    // Applicant fields
    if (applicant?.iban?.length > 27)
      errors.push("Το IBAN δεν μπορεί να ξεπερνά τους 27 χαρακτήρες.");
    if (applicant?.phone1?.length > 20)
      errors.push("Το τηλέφωνο 1 δεν μπορεί να ξεπερνά τους 20 χαρακτήρες.");
    if (usesLegacyPhoneFields && applicant?.phone2?.length > 20)
      errors.push("Το τηλέφωνο 2 δεν μπορεί να ξεπερνά τους 20 χαρακτήρες.");
    if (!usesLegacyPhoneFields && !isValidMobilePhone(applicant?.mobilePhone) && applicant?.mobilePhone)
      errors.push("Το κινητό τηλέφωνο πρέπει να ξεκινά από 69 και να αποτελείται από 10 ψηφία.");
    if (applicant?.city?.length > 50)
      errors.push("Η πόλη δεν μπορεί να ξεπερνά τους 50 χαρακτήρες.");
    if (applicant?.addressLine?.length > 100)
      errors.push("Η διεύθυνση δεν μπορεί να ξεπερνά τους 100 χαρακτήρες.");
    if (applicant?.postalCode?.length > 5)
      errors.push(
        "Ο ταχυδρομικός κώδικας δεν μπορεί να ξεπερνά τους 5 χαρακτήρες."
      );
    if (
      requireEmailVerification &&
      shouldRequireApplicantEmailVerification() &&
      !isApplicantEmailVerified()
    )
      errors.push("Παρακαλώ επιβεβαιώστε το email του δικαιούχου πριν την υποβολή.");

    // Husband fields (created/edited)
    if (husbands?.created || husbands?.updated) {
      const allHusbands = [
        ...(husbands.created || []),
        ...(husbands.updated || []),
      ];
      allHusbands.forEach((h, index) => {
        if (h.amka?.length > 11)
          errors.push(`Βρέθηκε ΑΜΚΑ γονέα που ξεπερνά τους 11 χαρακτήρες.`);
        if (h.ama?.length > 12)
          errors.push(`Βρέθηκε ΑΜΑ γονέα που ξεπερνά τους 12 χαρακτήρες.`);
        if (h.firstName?.length > 50)
          errors.push(`Βρέθηκε Όνομα γονέα που ξεπερνά τους 50 χαρακτήρες.`);
        if (h.lastName?.length > 50)
          errors.push(`Βρέθηκε Επώνυμο γονέα που ξεπερνά τους 50 χαρακτήρες.`);
      });
    }

    // Children fields (created/edited)
    if (children?.created || children?.updated) {
      const allChildren = [
        ...(children.created || []),
        ...(children.updated || []),
      ];
      allChildren.forEach((c, index) => {
        if (c.amka?.length > 11)
          errors.push(`Βρέθηκε ΑΜΚΑ τέκνου που ξεπερνά τους 11 χαρακτήρες.`);
        if (c.firstName?.length > 50)
          errors.push(`Βρέθηκε Όνομα τέκνου που ξεπερνά τους 50 χαρακτήρες.`);
        if (c.lastName?.length > 50)
          errors.push(`Βρέθηκε Επώνυμο τέκνου που ξεπερνά τους 50 χαρακτήρες.`);
      });
    }
    childrenHelp.forEach((child, index) => {
      if (Number(child?.category?.id) !== STUDENT_PLUS_18_CATEGORY_ID) return;

      const schoolSelection = childrenFileRefs.current[index]?.filebundles || child;
      const childLabel = `${child?.lastName ?? ""} ${child?.firstName ?? ""}`.trim();
      const prefix = childLabel
        ? `Παιδί ${childLabel}:`
        : `Παιδί ${index + 1}:`;

      if (!hasValue(schoolSelection?.schoolGeo?.id))
        errors.push(`${prefix} απαιτείται Δήμος.`);
      if (!hasValue(schoolSelection?.schoolType?.id))
        errors.push(`${prefix} απαιτείται Τύπος Σχολείου.`);
      if (!hasValue(schoolSelection?.school?.id))
        errors.push(`${prefix} απαιτείται Σχολείο.`);
      if (!hasValue(schoolSelection?.schoolClass?.id))
        errors.push(`${prefix} απαιτείται Τάξη.`);
      if (!hasValue(schoolSelection?.schoolRegNumber))
        errors.push(`${prefix} απαιτείται Α.Μ. Μαθητή.`);
    });

    return errors;
  };

  const formatBundles = () => { };

  const getChildrenFiles = async () => {
    if (childrenFileRefs.current) {
      await Promise.all(
        childrenFileRefs.current.map(async (childRef) => {
          if (
            childRef &&
            childRef.filebundles?.files
              ?.filter((file) => file?.id)
              ?.map((file) => file.id)?.length > 0
          ) {
            await childRef.uploadFiles();
          }
        })
      );
    }
    const childrenFiles = [];
    childrenFileRefs.current.forEach((childRef) => {
      if (childRef) {
        const files =
          childRef.filebundles?.files
            ?.filter((file) => file?.id)
            ?.map((file) => file.id) || [];
        const hasSchoolSelectionPayload =
          childRef.filebundles?.hasSchoolSelectionPayload;
        // childRef.saveData();
        if (files.length > 0 || hasSchoolSelectionPayload)
          childrenFiles.push({
            childId: childRef.filebundles.childId,
            bundles: files,
            hasSchoolSelectionPayload,
            ...(hasSchoolSelectionPayload
              ? {
                schoolGeo: childRef.filebundles.schoolGeo,
                schoolType: childRef.filebundles.schoolType,
                school: childRef.filebundles.school,
                schoolClass: childRef.filebundles.schoolClass,
                schoolRegNumber: childRef.filebundles.schoolRegNumber,
              }
              : {}),
          });
      }
    });

    return childrenFiles;
  };

  const getApplicationFiles = async () => {
    let applicationFiles;

    await applicationFileRef.current.uploadFiles();

    if (applicationFileRef) {
      // childRef.saveData();
      if (
        applicationFileRef.current?.filebundles?.files
          ?.filter((file) => file?.id)
          ?.map((file) => file.id)?.length > 0
      )
        applicationFiles =
          applicationFileRef.current?.filebundles?.files
            ?.filter((file) => file?.id)
            ?.map((file) => file.id) || [];
    }
    return applicationFiles;
  };

  const formatDataWithoutBundles = async () => {
    return getApplicationFormValues();
  };

  const formatDataWithBundles = async () => {
    const data = getApplicationFormValues();
    const childrenFiles = await getChildrenFiles();
    const applicationFiles = await getApplicationFiles();

    const attachBundles = (childrenArray) => {
      return childrenArray.map((child) => {
        const fileMatch = childrenFiles.find(
          (file) => file.childId === child.id
        );
        const childWithBundles = {
          ...child,
          bundles: fileMatch ? fileMatch.bundles : [],
        };

        if (fileMatch?.hasSchoolSelectionPayload) {
          childWithBundles.schoolGeo = fileMatch.schoolGeo;
          childWithBundles.schoolType = fileMatch.schoolType;
          childWithBundles.school = fileMatch.school;
          childWithBundles.schoolClass = fileMatch.schoolClass;
          childWithBundles.schoolRegNumber = fileMatch.schoolRegNumber;
        }

        return childWithBundles;
      });
    };

    // Attach bundles to all children
    const allChildrenWithBundles = attachBundles(
      data.childrenHelp
    );

    // Get ids from form state
    const createdIds = data.children.created.map((c) => c.id);
    const updatedIds = data.children.updated.map((c) => c.id);

    // Build created from bundles-attached objects
    const createdChildren = allChildrenWithBundles.filter((child) =>
      createdIds.includes(child.id)
    );

    // Build updated from bundles-attached objects
    const updatedChildrenFromForm = allChildrenWithBundles.filter((child) =>
      updatedIds.includes(child.id)
    );

    // Detect children that got bundles or a school selection but are not yet marked updated
    const childrenWithAttachmentChanges = allChildrenWithBundles.filter(
      (child) => {
        const fileMatch = childrenFiles.find(
          (file) => file.childId === child.id
        );
        return (
          (child.bundles && child.bundles.length > 0) ||
          fileMatch?.hasSchoolSelectionPayload
        );
      }
    );
    const newlyUpdatedChildren = childrenWithAttachmentChanges.filter(
      (child) =>
        !updatedIds.includes(child.id) && !createdIds.includes(child.id)
    );

    // Final updated = union of form updated + newly updated (from bundles)
    const updatedChildren = [
      ...updatedChildrenFromForm,
      ...newlyUpdatedChildren,
    ];

    const mergedValues = {
      ...data,
      bundles: applicationFiles || [],
      children: {
        created: createdChildren,
        updated: updatedChildren,
        deleted: data?.children?.deleted || [],
      },
    };

    return mergedValues;
  };

  const handleApplicationUpdate = async (
    action,
    requestJson,
    forceSave = false,
    navigateBack = false
  ) => {
    const requireApplicantEmailVerification = () => {
      setEmailVerificationRequired(true);
      form.setValue("applicantEmailVerification.verifiedEmail", "", {
        shouldDirty: false,
        shouldValidate: false,
      });
      form.setValue("applicantEmailVerification.isVerified", false, {
        shouldDirty: false,
        shouldValidate: false,
      });
      form.setValue("applicantEmailVerification.code", "", {
        shouldDirty: false,
        shouldValidate: false,
      });
      form.setValue("applicantEmailVerification.codeSentEmail", "", {
        shouldDirty: false,
        shouldValidate: false,
      });
    };

    try {
      setLoading(true);
      queryClient.invalidateQueries(["application-files", id]);
      queryClient.invalidateQueries(["files"]);
      if (requestJson?.reviews?.applicant === null)
        requestJson.reviews.applicant = {};
      if (requestJson?.reviews?.husbands === null)
        requestJson.reviews.husbands = {};
      if (requestJson?.reviews?.children === null)
        requestJson.reviews.children = {};
      const res = await updateApplication({
        ...requestJson,
        id,
        action,
        forceSave,
      });

      if (res?.status === "success") {
        setEmailVerificationRequired(false);
        if (navigateBack) {
          try {
            navigate(-1);
            return;
          } catch {
            navigate("/applications-navigate");
          }
        }
        await fetchApplication();

        setOpenDialog(false);
      }
      if (res?.status === "warning") {
        if (requestJson?.children) {
          requestJson.children.created = [];
          requestJson.children.updated = [];
          requestJson.children.deleted = [];
        }
        if (requestJson?.husbands) {
          requestJson.husbands.created = [];
          requestJson.husbands.updated = [];
          requestJson.husbands.deleted = [];
        }
        setOpenDialog(false);
        setDialogProps({
          title: "Υποβολή Αίτησης",
          content: res?.message,
          extraButtons: res?.data?.actions?.map((action) => ({
            action: action?.action,
            txt: action?.txt,
            onAction: async () => {
              await handleApplicationUpdate(action?.action, requestJson, true);
            },
          })),
          onConfirm: () => { },
        });
        setOpenDialog(true);
        // setOpenDialog(false);
        //await fetchApplication();
      }
      if (res?.status === "error") {
        setErrorBanner(res?.message);
        if (action === "submit") await resetForm();
        if (action === "submit" && isEmailVerificationError(res?.message)) {
          requireApplicantEmailVerification();
        }
        setOpenDialog(false);
      }

      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
      setErrorBanner("<div>Προέκυψε Σφάλμα!</div>");
      setOpenDialog(false);
    }
  };

  const handleOpenDialog = (actionType) => {
    switch (actionType) {
      case "accept":
        setDialogProps({
          title: "Αποδοχή Τροποποιήσεων",
          content: "Θέλετε σίγουρα να αποδεχτείτε τις τροποποιήσεις;",
          onConfirm: handleAccept,
        });
        break;
      case "refuse":
        setDialogProps({
          title: "Απόρριψη Τροποποιήσεων",
          content: "Θέλετε σίγουρα να απορρίψετε τις τροποποιήσεις;",
          onConfirm: handleRefuse,
        });
        break;
      case "approve":
        setDialogProps({
          title: "Έγκριση Αίτησης",
          content: "Θέλετε σίγουρα να εγκρίνετε την αίτηση;",
          onConfirm: handleApprove,
          commentsOn: settings?.approve?.commentsOn,
          withFileUpload: settings?.approve?.withFileUpload,
          fileDatakey: settings?.approve?.fileDataKey,
          fileDataValue: settings?.approve?.fileDataValue,
        });
        break;
      case "forResubmit":
        setDialogProps({
          title: "Επιστροφή Αίτησης για Διορθώσεις",
          content: "Θέλετε σίγουρα να επιστρέψετε την αίτηση για διορθώσεις στον δικαιούχο;",
          onConfirm: handleForResubmit,
          commentsOn: settings?.forResubmit?.commentsOn,
          withFileUpload: settings?.forResubmit?.withFileUpload,
          fileDatakey: settings?.forResubmit?.fileDataKey,
          fileDataValue: settings?.forResubmit?.fileDataValue,
        });
        break;
      case "reject":
        setDialogProps({
          title: "Απόρριψη Αίτησης",
          content: ` Θέλετε σίγουρα να επιστρέψετε την αίτηση στον δικαιούχο; 
            Παρακαλώ αναφέρετε τους λόγους απόρριψης.`,
          onConfirm: settings?.reject?.withFileUpload
            ? handleRejectWithFileUpload
            : handleReject,
          commentsOn: true,
          withFileUpload: settings?.reject?.withFileUpload,
          fileDatakey: settings?.reject?.fileDataKey,
          fileDataValue: settings?.reject?.fileDataValue,
        });
        break;
      case "submit":
        setDialogProps({
          title: "Υποβολή Αίτησης",
          content:
            "<strong>ΠΡΟΣΟΧΗ!</strong><br/> Μετά την Υποβολή της αίτησης δεν επιτρέπεται μεταβολή σε κανένα στοιχείο της! Βεβαιωθείτε για τα στοιχεία της αίτησής σας.",
          onConfirm: handleSubmit,
          withFileUpload: settings?.submit?.withFileUpload,
          fileDatakey: settings?.submit?.fileDataKey,
          fileDataValue: settings?.submit?.fileDataValue,
        });
        break;
      case "cancel":
        setDialogProps({
          title: "Διαγραφή / Υπαναχώρηση",
          content:
            "Θέλετε σίγουρα να προχωρήσετε σε Διαγραφή / Υπαναχώρηση της αίτησης;",
          onConfirm: handleCancel,
        });
        break;
      case "backToHome":
        const dirtyFields = Object.keys(form.formState.dirtyFields).map(
          (x) => ({
            field: x,
            value: form.getValues(x),
          })
        );
        if (!!dirtyFields?.length || control.isDirty)
          setDialogProps({
            title: "Προσοχή!",
            content:
              "Έχετε πραγματοποιήσει αλλαγές στη φόρμα σας. Αν αποχωρήσετε οι αλλαγές θα χαθούν. Θέλετε να κλείσετε τη σελίδα;",
            onConfirm: () => navigate("/applications-navigate"),
          });
        else navigate("/applications-navigate");
        break;
      case "partiallyApprove":
        setDialogProps({
          title: settings?.partiallyApprove?.partiallyApproveBtnTxt,
          content:
            "Θέλετε σίγουρα να προχωρήσετε σε αλλαγή κατάστασης της αίτησης;",
          onConfirm: handlePartiallyApprove,
          withFileUpload: settings?.partiallyApprove?.withFileUpload,
          fileDatakey: settings?.partiallyApprove?.fileDataKey,
          fileDataValue: settings?.partiallyApprove?.fileDataValue,
        });
        break;
      default:
        return;
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = async () => {
    await setOpenDialog(false);
    await setDialogProps((prev) => ({
      ...prev,
      withFileUpload: false,
      extraButtons: [],
    }));
  };

  const handlePreviewFile = async (file) => {
    const res = await downloadFile(file?.id, file?.type ?? file?.contentType);
    setFileToPreview(res);
  };

  // Refactored handlers

  const handleChangeIbanSave = async (newIban) => {
    const requestJson = await formatDataWithoutBundles();

    requestJson.applicant.iban = newIban;
    const res = await handleApplicationUpdate(
      "change_iban",
      requestJson,
    );

  }


  const handleSave = async () => {
    try {
      const requestJson = await formatDataWithBundles();
      // const { bundleIds } = formatBundles();
      await handleApplicationUpdate("save", requestJson);
    } catch {
      setLoading(false);
      setErrorBanner("<div>Προέκυψε σφάλμα!</div>");
    }
  };

  const handleAccept = async () => {
    await handleApplicationUpdate(
      "accept",
      { id: form.getValues("id") },
      false
    );
  };

  const handleRefuse = async () => {
    await handleApplicationUpdate(
      "refuse",
      { id: form.getValues("id") },
      false
    );
  };

  const handleApproveSave = async () => {
    try {
      const requestJson = await formatDataWithBundles();
      // const { bundleIds } = formatBundles();
      await handleApplicationUpdate("save", requestJson);

      // setLoading(true);

      // ToDo SOS Uncomment this
      // const resFiles = await control.uploadFileGroups();
      // if (resFiles?.status === "error") {
      //   setErrorBanner(resFiles?.message);
      //   setLoading(false);
      //   return;
      // }
      // await fetchApplication();

      // setLoading(false);
    } catch {
      setLoading(false);
      setErrorBanner("<div>Προέκυψε σφάλμα!</div>");
    }
  };

  const handleApprove = async (comments) => {
    const requestJson = await formatDataWithBundles();
    if (comments) requestJson.comments = comments;
    await handleApplicationUpdate("approve", requestJson, false, true);
  };

  const handleForResubmit = async (comments) => {
    const requestJson = await formatDataWithBundles();
    if (comments) requestJson.comments = comments;
    await handleApplicationUpdate("for_resubmit", requestJson, false, true);
  };

  const handleRejectWithFileUpload = async (comments) => {
    const { bundleIds } = formatBundles();
    const additionalData =
      fileBundles?.length > 0
        ? {
          id: form.getValues("id"),
          action: "reject",
          comments,
          bundles: fileBundles,
        }
        : {
          id: form.getValues("id"),
          action: "reject",
          comments,
        };
    await handleApplicationUpdate("reject", additionalData, false);
  };

  const handleReject = async (comments) => {
    const requestJson = await formatDataWithBundles();
    if (comments) requestJson.comments = comments;
    await handleApplicationUpdate("reject", requestJson, false, true);
  };

  const handleSubmit = async (comments) => {
    const requestJson = await formatDataWithBundles();
    if (comments) requestJson.comments = comments;
    await handleApplicationUpdate("submit", requestJson);
  };

  const handleCancel = async (comments) => {
    const requestJson = await formatDataWithoutBundles();
    if (comments) requestJson.comments = comments;
    await handleApplicationUpdate("cancel", requestJson);
  };

  const handlePartiallyApprove = async (fileBundles = []) => {
    await handleApplicationUpdate(
      "partially_approve",
      fileBundles?.length > 0
        ? {
          bundles: fileBundles,
        }
        : {},
      false
    );
  };

  return (
    <FormProvider {...form}>
      <FilePreviewer
        file={fileToPreview}
        onClose={() => setFileToPreview(null)}
      />
      <ConfirmDialog
        open={openDialog}
        title={dialogProps.title}
        content={dialogProps.content}
        onClose={handleCloseDialog}
        onConfirm={dialogProps.onConfirm}
        commentsOn={dialogProps.commentsOn}
        withFileUpload={dialogProps?.withFileUpload}
        fileDatakey={dialogProps?.fileDatakey}
        fileDataValue={dialogProps?.fileDataValue}
        extraButtons={dialogProps?.extraButtons}
      />
      <div>
        <AlertBanner canClose={false} info={data?.application?.status}>
          <div style={{ marginTop: 4, marginBottom: 4 }}>
            {data?.application?.status?.files?.length > 0 &&
              data?.application?.status?.files.map((file, index) => (
                <ListFileItem
                  key={index}
                  file={file}
                  index={index}
                  handlePreviewFile={() => handlePreviewFile(file)}
                />
              ))}
          </div>

          <div className="buttons-container">
            {data?.application?.status?.refuseBtnText && (
              <Button
                variant="contained"
                color="error"
                onClick={() => handleOpenDialog("refuse")}
              >
                {data?.application?.status?.refuseBtnText}
              </Button>
            )}
            {data?.application?.status?.acceptBtnText && (
              <Button
                variant="contained"
                onClick={() => handleOpenDialog("accept")}
              >
                {data?.application?.status?.acceptBtnText}
              </Button>
            )}
          </div>
        </AlertBanner>
        <div
          style={{
            display: "flex",
            width: "100%",
            marginLeft: "auto",
            marginTop: "2px",
          }}
        >
          <a
            style={{ marginLeft: "auto" }}
            className=" ds-link small-font-size "
            href={`${url}/documents/ΛΑΕ-ΟΠΕΚΑ Τρίτεκνες-Πολύτεκνες Μητέρες - Οδηγίες Χρήσης για την Υποβολή Αίτησης.pdf`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Βοήθεια / Οδηγίες Χρήσης
          </a>
        </div>
        <div
          style={{
            display: "flex",
            width: "100%",
            marginLeft: "auto",
            marginTop: "4px",
          }}
        >
          <a
            style={{ marginLeft: "auto" }}
            className=" ds-link small-font-size "
            href="mailto:lae-miteres@opeka.gr"
            target="_blank"
            rel="noopener noreferrer"
          >
            Επικοινωνήστε με e-mail με την Υπηρεσία ΛΑΕ ΟΠΕΚΑ
          </a>
        </div>
      </div>
      <SummaryCard
        protocolNumber={data?.application?.protocolNumber}
        protocolNumber2={data?.application?.protocolNumber2}
        status={data?.application?.status}
        type={data?.application?.type}
        created={data?.application?.created}
        updated={data?.application?.updated}
        submitted={data?.application?.submitted}
        season={data?.application?.season?.name}
      />
      <MothersInfo
        applicant={data?.application?.applicant}
        review={data?.application?.reviews?.applicant}
        canEdit={settings?.applicant?.canEdit}
        canApprove={settings?.applicant?.canApprove}
        canPartiallyEdit={settings?.applicant?.canPartiallyEdit}
        canViewReview={settings?.applicant?.canViewReview}
        canViewWSResult={settings?.applicant?.canViewWSResult}
        canEditIban={settings?.applicant?.canEditIban}
        useLegacyPhoneFields={usesLegacyPhoneFields}
        isLoadedEmailVerified={isLoadedApplicantEmailVerified}
        shouldShowEmailVerification={shouldShowEmailVerification}
        forceEmailVerification={emailVerificationRequired}
        onEmailVerified={() => setEmailVerificationRequired(false)}
      />

      {/* <FathersTable /> */}
      <Fathers
        husbands={data?.application?.husbands}
        review={data?.application?.reviews?.husbands}
        settings={settings?.husbands}
      />
      <Children
        children={data?.application?.children}
        settings={settings?.children}
        review={data?.application?.reviews?.children}
      />
      <ApplicationAttachments
        ref={(el) => (applicationFileRef.current = el)}
        settings={data?.application?.settings?.attachments}
      />
      {form.watch("childrenHelp")?.length > 0 &&
        form.watch("childrenHelp")?.map((child, index) => {
          if (child?.category?.fileDataKey && child?.category?.fileDataValue) {
            return (
              <ChildAttachments
                key={child?.id ?? index}
                child={child}
                dataKey={child?.category?.fileDataKey}
                dataValue={child?.category?.fileDataValue}
                season={data?.application?.season}
                lookups={data?.lookups}
                applicantMunicipality={applicantMunicipality}
                formIndex={index}
                ref={(el) => (childrenFileRefs.current[index] = el)}
                settings={data?.application?.settings?.attachments}
              />
            );
          }
        })}
      {/* <Attachments fileGroups={fileGroups} settings={settings?.attachments} /> */}
      <ApplicationActions
        handleCancel={() => handleOpenDialog("cancel")}
        canEdit={settings?.canEdit}
        canCancel={settings?.cancel?.can}
        canSubmit={settings?.submit?.can}
        settings={settings}
        handleSubmit={() => {
          const errors = handleValidate({ requireEmailVerification: true });

          if (errors?.length > 0) {
            setErrorBanner(
              `<div>
                ${errors
                ?.map((error, index) => `<p>${error}</p>`)
                .join("")}
              </div>`
            );
          } else handleOpenDialog("submit");
        }}
        handleApprove={() => {
          handleOpenDialog("approve");
        }}
        handleSave={async () => {
          const errors = handleValidate();

          if (errors?.length > 0) {
            setErrorBanner(
              `<div>
                ${errors
                ?.map((error, index) => `<p>${error}</p>`)
                .join("")}
              </div>`
            );
          } else await handleSave();
        }}
        handleApproveSave={() => handleApproveSave()}
        handleReject={() => handleOpenDialog("reject")}
        handlePartiallyApprove={() => handleOpenDialog("partiallyApprove")}
        handleAmendment={() => setOpenAmendment(true)}
        handleChangeIban={() => setOpenChangeIban(true)}
        handleBackToHome={() => handleOpenDialog("backToHome")}
        handleForResubmit={() => handleOpenDialog("forResubmit")}
      />
      <AlertBanner
        openInfo={!!errorBanner}
        setOpenMessage={setErrorBanner}
        info={{
          message: `<div>${errorBanner}</div>`,
          cssClass: "error",
        }}
      />
      {/* <Button onClick={() => handleSave()} variant="contained" color="primary">
        Log Form Values
      </Button> */}
      <History
        history={data?.application?.history}
        handlePreviewFile={handlePreviewFile}
      />
      <Amendment
        open={openAmendment}
        handleClose={() => setOpenAmendment(false)}
      />
      <ChangeIban
        open={openChangeIban}
        initialIban={data?.application?.applicant?.iban}
        onClose={() => setOpenChangeIban(false)}
        handleChangeIbanSave={handleChangeIbanSave}
      />
    </FormProvider>
  );
};

export default ApplicationDetails;
